import { useMemo } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "ck_utm";
const KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];
const MAX_LEN = 200;

const readStored = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Parse UTM / click-id params from the URL once, persist them in
 * sessionStorage (so they survive in-page navigation and the `?p=` rewrite),
 * and return a compact map for the lead document:
 *   { source, medium, campaign, term, content, gclid, fbclid }
 * Missing keys are omitted. Returns {} when nothing is known. CK-1057 on failure.
 */
export default function useUtmParams() {
  const { search } = useLocation();

  return useMemo(() => {
    try {
      const params = new URLSearchParams(search);
      const fromUrl = {};
      KEYS.forEach((k) => {
        const v = params.get(k);
        if (v && v.trim()) fromUrl[k.replace(/^utm_/, "")] = v.trim().slice(0, MAX_LEN);
      });

      if (Object.keys(fromUrl).length > 0) {
        try {
          window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
        } catch {
          /* private mode — fine, we still return the URL values */
        }
        return fromUrl;
      }
      return readStored() || {};
    } catch (error) {
      console.error("[CK-1057] useUtmParams parse failed", {
        errorCode: "CK-1057",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      return {};
    }
  }, [search]);
}
